import { Chart, registerables } from 'chart.js';
import chartTheme from '../styles/chartTheme';
import React, { useEffect, useRef, useMemo } from 'react';

Chart.register(...registerables);

export default function DistributionTab({ entries, avgFreq }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const numsByFreq = useMemo(() => {
    const map = {};
    entries.forEach(({ n, f }) => {
      if (!map[f]) map[f] = [];
      map[f].push(n);
    });
    return map;
  }, [entries]);

  const { distData, median, stdDev, skewness } = useMemo(() => {
    if (!entries.length) return { distData: [], median: 0, stdDev: 0, skewness: 0 };

    // Build frequency distribution
    const freqDist = {};
    entries.forEach(({ f }) => {
      freqDist[f] = (freqDist[f] || 0) + 1;
    });
    const dd = Object.entries(freqDist)
      .map(([freq, count]) => ({ freq: +freq, count }))
      .sort((a, b) => a.freq - b.freq);

    // Median
    const sorted = [...entries].sort((a, b) => a.f - b.f);
    const mid = Math.floor(sorted.length / 2);
    const med = sorted.length % 2 !== 0
      ? sorted[mid].f
      : Math.round((sorted[mid - 1].f + sorted[mid].f) / 2);

    // Standard deviation
    const mean = entries.reduce((a, e) => a + e.f, 0) / entries.length;
    const variance = entries.reduce((a, e) => a + Math.pow(e.f - mean, 2), 0) / entries.length;
    const sd = Math.sqrt(variance);

    // Skewness (Fisher-Pearson)
    const n = entries.length;
    const m3 = entries.reduce((a, e) => a + Math.pow(e.f - mean, 3), 0) / n;
    const sk = sd > 0 ? m3 / Math.pow(sd, 3) : 0;

    return { distData: dd, median: med, stdDev: sd, skewness: sk };
  }, [entries]);

  useEffect(() => {
    if (!chartRef.current || !distData.length) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const t = chartTheme();

    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels: distData.map(d => d.freq),
        datasets: [{
          data: distData.map(d => d.count),
          backgroundColor: distData.map(d =>
            d.freq === avgFreq ? 'rgba(74, 222, 128, 0.7)' : 'rgba(99, 102, 241, 0.5)'
          ),
          borderColor: distData.map(d =>
            d.freq === avgFreq ? '#4ade80' : '#6366f1'
          ),
          borderWidth: 1,
          borderRadius: 3,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => `Drawn ${items[0].label} times`,
              label: (item) => {
                const freq = distData[item.dataIndex]?.freq;
                const nums = (numsByFreq[freq] || []).slice().sort((a, b) => a - b);
                return `Numbers: ${nums.join(', ')}`;
              },
            },
          },
        },
        scales: {
          x: {
            title: { display: true, text: 'Times drawn', color: t.title, font: { size: 11, family: t.fontFamily } },
            ticks: { font: { size: 10, family: t.fontFamily }, color: t.tick, autoSkip: true, maxTicksLimit: 30 },
            grid: { display: false },
          },
          y: {
            title: { display: true, text: 'Number of balls', color: t.title, font: { size: 11, family: t.fontFamily } },
            ticks: { font: { size: 11, family: t.fontFamily }, color: t.tick, stepSize: 1 },
            grid: { color: t.grid },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [distData, avgFreq, numsByFreq]);

  return (
    <>
      <p className="section-label">Frequency distribution analysis</p>
      <div className="dist-stats">
        <div className="dist-stat">
          <span className="dist-stat-label">Mean</span>
          <span className="dist-stat-val">{avgFreq}</span>
        </div>
        <div className="dist-stat">
          <span className="dist-stat-label">Median</span>
          <span className="dist-stat-val">{median}</span>
        </div>
        <div className="dist-stat">
          <span className="dist-stat-label">Std Dev</span>
          <span className="dist-stat-val">{stdDev.toFixed(1)}</span>
        </div>
        <div className="dist-stat">
          <span className="dist-stat-label">Skewness</span>
          <span className="dist-stat-val" style={{ color: Math.abs(skewness) > 0.5 ? '#f59e0b' : '#4ade80' }}>
            {skewness.toFixed(2)}
          </span>
        </div>
      </div>
      <div className="chart-wrap">
        <canvas ref={chartRef}></canvas>
      </div>
      <p className="dist-explanation">
        Each bar shows how many numbers share that draw count. Hover or tap a bar to see which numbers.
        <span style={{ color: '#4ade80' }}> Green bar</span> = mean ({avgFreq} draws).
      </p>
      <div className="dist-number-list">
        {distData.map(d => (
          <div key={d.freq} className="dist-number-row">
            <span className="dist-number-freq" style={{ color: d.freq === avgFreq ? '#4ade80' : '#6366f1' }}>
              {d.freq}×
            </span>
            <span className="dist-number-vals">
              {(numsByFreq[d.freq] || []).slice().sort((a, b) => a - b).join(', ')}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
